// Libraries
import { Box } from '@chakra-ui/react';
import { signIn, useSession } from 'next-auth/react';
import { useState } from 'react';
import { toFormikValidationSchema } from 'zod-formik-adapter';
// Components
import { FormError, FormSubmit } from '@atoms/index';
import { useForm } from '@hooks/index';
import { FormInput } from '@molecules/index';
// Data
import { LoginSchema, LoginValues } from '@schemas/forms';
// Utils
import { AUTH_PROVIDER } from '@constants/index';
import text from '@translations/en.json';

const initialValues: LoginValues = {
  username: '',
  password: '',
};

/**
 * LoginForm component represents a form for user login.
 * @returns {ReactElement} The sidebar component.
 */
export const LoginForm = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const buttonMt = !!errorMessage ? 3 : 11;
  const { status } = useSession();

  /**
   * Handles form submission.
   * @param {LoginValues} values - The form values to be submitted.
   */
  const submitHandler = async (values: LoginValues) => {
    setIsLoading(true);
    signIn(AUTH_PROVIDER, { redirect: false, ...values }).then((response) => {
      if (!response?.ok) {
        setIsLoading(false);
        setErrorMessage(response?.error ?? text.invalid_username);
      }
    });
  };

  const formikCtx = useForm<LoginValues>({
    initialValues,
    schema: toFormikValidationSchema(LoginSchema),
    submitHandler,
  });
  const { submitCount } = formikCtx;

  return (
    <Box
      as='form'
      display='flex'
      flexDirection='column'
      alignItems='center'
      gap={3.5}
      onSubmit={formikCtx.handleSubmit}
      w={72}
      data-testid='login-form'
    >
      <FormInput
        isRequired
        label={text.username_field_label}
        wasSubmitted={submitCount > 0}
        id='username'
        autoComplete='username'
        value={formikCtx.values.username}
        onChange={formikCtx.handleChange}
        error={formikCtx.errors.username}
      />
      <FormInput
        isRequired
        label={text.password_field_label}
        wasSubmitted={submitCount > 0}
        id='password'
        autoComplete='current-password'
        value={formikCtx.values.password}
        onChange={formikCtx.handleChange}
        error={formikCtx.errors.password}
        isPassword
      />
      {!!errorMessage && <FormError>{errorMessage}</FormError>}
      <FormSubmit mt={buttonMt} isLoading={isLoading || status === 'authenticated'} loadingText={text.logging_in}>
        {text.log_in}
      </FormSubmit>
    </Box>
  );
};
